import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../App';
import { Calculator, Users, FileText, TrendingUp, CheckCircle, ArrowRight, Building2, BarChart3, MessageSquare, Smartphone, Bot, GraduationCap, Zap, Heart, Bell, LineChart } from 'lucide-react';
import Logo from '../components/Logo';
import { isNativePlatform } from '../lib/platform';

const LandingPage = () => {
  const { user } = useAuth();
  const isNative = isNativePlatform();

  const features = [
    {
      icon: Calculator,
      title: "Calculateur de Rentabilité",
      description: "Analysez vos investissements avec précision. Rendement brut, net, cash-flow et tous les régimes fiscaux français.",
      tier: "free",
      tierLabel: "Gratuit"
    },
    {
      icon: Building2,
      title: "Outil Marchand de Biens",
      description: "Calculez le prix d'achat maximum, estimez vos profits et identifiez les points de vigilance.",
      tier: "free",
      tierLabel: "Gratuit"
    },
    {
      icon: BarChart3,
      title: "Comparateur Fiscal",
      description: "Comparez tous les régimes : Micro-BIC, Réel, LMNP, LMP, SCI IS/IR pour optimiser votre fiscalité.",
      tier: "free",
      tierLabel: "Gratuit"
    },
    {
      icon: Users,
      title: "Communauté d'Investisseurs",
      description: "Échangez avec des investisseurs expérimentés, partagez vos expériences et progressez ensemble.",
      tier: "free",
      tierLabel: "Gratuit (lecture)"
    },
    {
      icon: FileText,
      title: "Dossier Bancaire PDF",
      description: "Générez un dossier professionnel pour votre banquier en quelques clics.",
      tier: "club",
      tierLabel: "Club 10M"
    },
    {
      icon: Bot,
      title: "Agent IA Immobilier",
      description: "Notre IA recherche et analyse les annonces immobilières selon vos critères d'investissement.",
      tier: "club",
      tierLabel: "Club 10M"
    },
    {
      icon: LineChart,
      title: "Statistiques & Évolution des Prix",
      description: "Suivez l'évolution des prix au m² dans vos zones de recherche et analysez vos performances.",
      tier: "club",
      tierLabel: "Club 10M"
    },
    {
      icon: Heart,
      title: "Biens Immobiliers",
      description: "Publiez vos biens et découvrez les opportunités des autres membres du Club.",
      tier: "club",
      tierLabel: "Club 10M"
    },
    {
      icon: GraduationCap,
      title: "Formations Exclusives",
      description: "Accédez à des cours vidéo sur l'investissement immobilier dispensés par des experts.",
      tier: "club",
      tierLabel: "Club 10M"
    },
    {
      icon: MessageSquare,
      title: "Messages Privés",
      description: "Contactez directement les membres de la communauté pour des conseils personnalisés.",
      tier: "club",
      tierLabel: "Club 10M"
    },
    {
      icon: Users,
      title: "Annuaire des Membres",
      description: "Trouvez des investisseurs et professionnels de l'immobilier dans toute la France.",
      tier: "club",
      tierLabel: "Club 10M"
    },
    {
      icon: Bell,
      title: "Alertes Email Automatiques",
      description: "Recevez des alertes quand un nouveau bien correspond à vos critères de recherche IA.",
      tier: "club",
      tierLabel: "Club 10M"
    },
    {
      icon: Zap,
      title: "Intégrations",
      description: "Connectez Calendly pour vos RDV et Zapier pour automatiser vos workflows.",
      tier: "club",
      tierLabel: "Club 10M"
    }
  ];

  const testimonials = [
    {
      name: "Margot.",
      role: "25 ans - Lyon",
      content: "10M Investor m'a permis d'être très efficace et professionnelle dans mes recherches. Les outils et le réseau sont d'une aide précieuse. Je recommande vivement !"
    },
    {
      name: "Alexandre.",
      role: "30 ans - Paris",
      content: "Utiliser 10M Investor est un vrai plaisir ! De bons outils d'analyse, une interface dynamique et intuitive, avec un côté humain rare dans le secteur."
    },
    {
      name: "Charlène.",
      role: "32 ans - Lyon",
      content: "Service efficace, accès facile et une plateforme complète. Tout est géré avec professionnalisme, ça change tout pour mes investissements !"
    },
    {
      name: "R.",
      role: "25 ans - Lyon",
      content: "Un accompagnement exemplaire : efficacité, professionnalisme, disponibilité. L'outil MDB et le calculateur donnent de précieux repères. Grâce à 10M Investor, mon projet s'est déroulé en toute sérénité."
    },
    {
      name: "Julien.",
      role: "27 ans - Lyon",
      content: "Merci 10M Investor pour la réactivité et le professionnalisme des outils sur tous mes projets. Je recommande vivement !"
    },
    {
      name: "Céline.",
      role: "43 ans - Lyon",
      content: "10M Investor m'a aidée à trouver la perle rare pour mon projet. En un mois, le compromis était signé ! Une opération réussie grâce aux analyses et à la parfaite connaissance du marché."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 sm:h-24">
            <Link to="/" className="flex items-center">
              {/* Logo horizontal unifié */}
              <img src="/logo-horizontal.png" alt="10 Millions Investor" className="h-16 sm:h-20 w-auto" />
            </Link>
            <div className="hidden md:flex items-center space-x-8">
              {!isNative && <Link to="/pricing" className="text-slate-600 hover:text-[#0D47A1] transition-colors font-medium">Tarifs</Link>}
              <a href="#features" className="text-slate-600 hover:text-[#0D47A1] transition-colors font-medium">Fonctionnalités</a>
              <a href="#testimonials" className="text-slate-600 hover:text-[#0D47A1] transition-colors font-medium">Témoignages</a>
            </div>
            <div className="flex items-center space-x-4">
              {user ? (
                <Link to="/dashboard">
                  <Button data-testid="go-to-dashboard-btn" className="bg-[#0D47A1] hover:bg-[#0A367A]">
                    Mon tableau de bord
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" data-testid="login-btn" className="text-slate-600 hover:text-[#0D47A1]">
                      Connexion
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button data-testid="register-btn" className="bg-[#0D47A1] hover:bg-[#0A367A]">
                      Commencer gratuitement
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-gradient py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeIn">
              <div className="inline-flex items-center px-4 py-2 bg-[#0D47A1]/10 border border-[#0D47A1]/20 rounded-full mb-6">
                <span className="w-2 h-2 bg-[#00B050] rounded-full mr-2 animate-pulse"></span>
                <span className="text-sm font-semibold text-[#0D47A1]">Club privé — Accès sur sélection</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight">
                Le club privé des
                <span className="text-[#0D47A1]"> investisseurs immobiliers</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
                10M Investor réunit investisseurs et professionnels de l'immobilier au sein d'un écosystème exclusif. Outils d'analyse avancés, réseau de confiance et intelligence artificielle au service de vos projets.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button data-testid="hero-cta-btn" size="lg" className="bg-[#0D47A1] hover:bg-[#0A367A] text-lg px-8 h-12">
                    Demander l'accès
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/calculator">
                  <Button data-testid="hero-demo-btn" variant="outline" size="lg" className="text-lg px-8 h-12 border-slate-300">
                    Tester les outils gratuits
                  </Button>
                </Link>
              </div>
              <div className="mt-8 flex items-center space-x-6">
                <div className="flex -space-x-2">
                  {[
                    "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
                    "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face",
                    "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face",
                    "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face"
                  ].map((img, i) => (
                    <img 
                      key={i} 
                      src={img}
                      alt={`Investisseur ${i + 1}`}
                      className="w-10 h-10 rounded-full border-2 border-white object-cover"
                    />
                  ))}
                </div>
                <p className="text-slate-600">
                  Rejoignez un cercle de <span className="font-semibold text-slate-900">+200 investisseurs</span> actifs
                </p>
              </div>
            </div>
            <div className="relative animate-slideInRight hidden lg:block">
              <div className="bg-white rounded-2xl shadow-2xl p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-slate-900">Analyse rapide</h3>
                  <span className="text-xs bg-[#00B050]/10 text-[#00B050] px-2 py-1 rounded-full font-medium">Rentable</span>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Prix d'achat</span>
                    <span className="font-mono font-semibold">200 000 €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Loyer mensuel</span>
                    <span className="font-mono font-semibold">900 €</span>
                  </div>
                  <div className="h-px bg-slate-200" />
                  <div className="flex justify-between">
                    <span className="text-slate-500">Rendement brut</span>
                    <span className="font-mono font-semibold text-[#00B050]">5.4%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Cash-flow mensuel</span>
                    <span className="font-mono font-semibold text-[#00B050]">+125 €</span>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 bg-[#0D47A1] text-white rounded-xl p-4 shadow-lg">
                <TrendingUp className="h-6 w-6" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Club Privé Section */}
      <section className="py-16 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M30 0L60 30L30 60L0 30z\' fill=\'%23ffffff\' fill-opacity=\'0.4\'/%3E%3C/svg%3E")'}}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <Badge className="bg-[#00B050]/20 text-[#00B050] border-[#00B050]/30 mb-4 text-sm px-4 py-1.5">
              Accès sélectif
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Pourquoi un club <span className="text-[#00B050]">privé</span> ?
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              10M Investor n'est pas une plateforme ouverte à tous. Nous sélectionnons nos membres pour garantir la qualité des échanges et la fiabilité du réseau.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
              <div className="w-12 h-12 rounded-xl bg-[#0D47A1]/30 flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-blue-300" />
              </div>
              <h3 className="text-lg font-bold mb-2">Réseau de confiance</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Investisseurs, agents immobiliers, courtiers, notaires, gestionnaires de patrimoine... Un cercle de professionnels vérifiés qui partagent des opportunités réelles.
              </p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
              <div className="w-12 h-12 rounded-xl bg-[#00B050]/30 flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-green-300" />
              </div>
              <h3 className="text-lg font-bold mb-2">Outils exclusifs</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Agent IA de recherche, statistiques de marché en temps réel, diagnostic climatique, données cadastrales et biens vendus — des outils réservés aux membres du Club.
              </p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
              <div className="w-12 h-12 rounded-xl bg-amber-500/30 flex items-center justify-center mb-4">
                <Heart className="h-6 w-6 text-amber-300" />
              </div>
              <h3 className="text-lg font-bold mb-2">Accompagnement humain</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Messages privés avec le fondateur, partage de biens entre membres, communauté d'entraide et veille personnalisée sur vos critères d'investissement.
              </p>
            </div>
          </div>
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-6 bg-white/5 border border-white/10 rounded-2xl px-8 py-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">25+</div>
                <div className="text-xs text-slate-400">Partenaires Pro</div>
              </div>
              <div className="w-px h-10 bg-white/20"></div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-400">100%</div>
                <div className="text-xs text-slate-400">Profils vérifiés</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
              Tous les outils pour réussir vos investissements
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
              De l'analyse à la négociation, en passant par la fiscalité et la communauté.
            </p>
            <div className="flex items-center justify-center gap-4 mt-6">
              <div className="flex items-center gap-2">
                <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">Gratuit</Badge>
                <span className="text-sm text-slate-500">Accès libre pour tester</span>
              </div>
              {!isNative && (
              <div className="flex items-center gap-2">
                <Badge className="bg-gradient-to-r from-[#0D47A1] to-[#00B050] text-white hover:opacity-90">Club 10M</Badge>
                <span className="text-sm text-slate-500">1 997€ TTC · 12 mois · ou 4× 499,25€</span>
              </div>
              )}
            </div>
          </div>
          
          {/* Fonctionnalités Gratuites */}
          <div className="mb-12">
            <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm">🆓</span>
              Fonctionnalités Gratuites
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.filter(f => f.tier === 'free').map((feature, index) => (
                <Card key={index} className="border-slate-200 hover:border-slate-400 transition-all hover:shadow-md">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                        <feature.icon className="h-5 w-5 text-slate-700" />
                      </div>
                      <Badge variant="outline" className="text-xs bg-slate-50">{feature.tierLabel}</Badge>
                    </div>
                    <h4 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h4>
                    <p className="text-sm text-slate-600">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          
          {/* Fonctionnalités Club 10M - hidden on mobile (Apple 3.1.1) */}
          {!isNative && (
          <div>
            <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gradient-to-r from-[#0D47A1] to-[#00B050] flex items-center justify-center text-white text-sm">⭐</span>
              Fonctionnalités Club 10M Investor
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.filter(f => f.tier === 'club').map((feature, index) => (
                <Card key={index} className="border-slate-200 hover:border-[#0D47A1] transition-all hover:shadow-lg group">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#0D47A1]/10 to-[#00B050]/10 flex items-center justify-center group-hover:from-[#0D47A1]/20 group-hover:to-[#00B050]/20 transition-colors">
                        <feature.icon className="h-5 w-5 text-[#0D47A1]" />
                      </div>
                      <Badge className="text-xs bg-gradient-to-r from-[#0D47A1] to-[#00B050] text-white">{feature.tierLabel}</Badge>
                    </div>
                    <h4 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h4>
                    <p className="text-sm text-slate-600">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          )}
          
          {/* CTA */}
          {!isNative && (
          <div className="text-center mt-12">
            <Link to="/pricing">
              <Button size="lg" className="bg-gradient-to-r from-[#0D47A1] to-[#00B050] hover:opacity-90 text-base px-8 py-6">
                Voir tous les tarifs
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
          )}
        </div>
      </section>

      {/* Mobile App Section */}
      <section className="py-20 bg-gradient-to-br from-[#0D47A1] to-[#1565C0] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full mb-6">
                <Smartphone className="h-4 w-4 mr-2" />
                <span className="text-sm font-medium">Disponible sur mobile</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Investissez où que vous soyez
              </h2>
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                Téléchargez l'application <strong>10 Millions Investor</strong> sur Android et accédez à tous vos outils d'investissement en mobilité.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center text-blue-100">
                  <CheckCircle className="h-5 w-5 text-[#00B050] mr-3 flex-shrink-0" />
                  Calculateur de rentabilité instantané
                </li>
                <li className="flex items-center text-blue-100">
                  <CheckCircle className="h-5 w-5 text-[#00B050] mr-3 flex-shrink-0" />
                  Notifications sur les opportunités
                </li>
                <li className="flex items-center text-blue-100">
                  <CheckCircle className="h-5 w-5 text-[#00B050] mr-3 flex-shrink-0" />
                  Communauté et messagerie en temps réel
                </li>
                <li className="flex items-center text-blue-100">
                  <CheckCircle className="h-5 w-5 text-[#00B050] mr-3 flex-shrink-0" />
                  Synchronisation avec votre compte web
                </li>
              </ul>
              {!isNative && (
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://play.google.com/store/apps/details?id=com.tenminvestor.app" target="_blank" rel="noopener noreferrer" className="inline-block transition-transform hover:scale-105" data-testid="playstore-download-btn">
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/Google_Play_Store_badge_EN.svg/1280px-Google_Play_Store_badge_EN.svg.png" 
                    alt="Disponible sur Google Play"
                    className="h-14"
                  />
                </a>
              </div>
              )}
            </div>
            <div className="relative flex justify-center lg:justify-end">
              {/* iPhone Mockup */}
              <div className="relative">
                <div className="w-[280px] h-[580px] bg-slate-900 rounded-[3rem] p-3 shadow-2xl transform rotate-[-5deg] hover:rotate-0 transition-transform duration-500">
                  <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                    {/* Notch */}
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-7 bg-slate-900 rounded-b-2xl z-20"></div>
                    {/* Screen Content with Real Estate Background */}
                    <div className="w-full h-full relative">
                      {/* Background Image */}
                      <div 
                        className="absolute inset-0 bg-cover bg-center"
                        style={{
                          backgroundImage: `url('https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400&q=80')`,
                        }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
                      </div>
                      
                      {/* Content Overlay */}
                      <div className="relative z-10 w-full h-full pt-10 flex flex-col">
                        {/* App Header */}
                        <div className="px-4 py-3">
                          <div className="flex items-center justify-between">
                            <img src="/logo-horizontal.png" alt="10M Investor" className="h-6 w-auto brightness-0 invert" />
                            <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-white text-xs font-bold">
                              P
                            </div>
                          </div>
                        </div>
                        
                        {/* Hero Text */}
                        <div className="px-4 pt-4">
                          <p className="text-white/70 text-xs font-medium uppercase tracking-wider">Votre prochain investissement</p>
                          <h3 className="text-white text-lg font-bold mt-1">Appartement T3 Lyon 6ème</h3>
                          <p className="text-white/80 text-sm mt-1">85m² • 2 chambres • Balcon</p>
                        </div>
                        
                        {/* Stats Cards */}
                        <div className="px-4 mt-4 space-y-2">
                          <div className="bg-white/15 backdrop-blur-md rounded-xl p-3 border border-white/20">
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="text-white/70 text-xs">Rendement brut</div>
                                <div className="text-white text-xl font-bold">6.2%</div>
                              </div>
                              <div className="w-10 h-10 rounded-full bg-[#00B050] flex items-center justify-center">
                                <TrendingUp className="h-5 w-5 text-white" />
                              </div>
                            </div>
                          </div>
                          <div className="bg-[#00B050]/90 backdrop-blur-md rounded-xl p-3">
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="text-white/90 text-xs">Cash-flow mensuel</div>
                                <div className="text-white text-xl font-bold">+245 €</div>
                              </div>
                              <div className="text-white/80 text-xs text-right">
                                <div>Autofinancé</div>
                                <div className="font-semibold">✓ Rentable</div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Bottom Actions */}
                        <div className="mt-auto px-4 pb-6">
                          <div className="grid grid-cols-3 gap-2">
                            <div className="bg-white/15 backdrop-blur rounded-lg py-2 text-center">
                              <Calculator className="h-4 w-4 text-white mx-auto mb-1" />
                              <div className="text-white text-[10px]">Calculer</div>
                            </div>
                            <div className="bg-white/15 backdrop-blur rounded-lg py-2 text-center">
                              <Building2 className="h-4 w-4 text-white mx-auto mb-1" />
                              <div className="text-white text-[10px]">MDB</div>
                            </div>
                            <div className="bg-white/15 backdrop-blur rounded-lg py-2 text-center">
                              <Users className="h-4 w-4 text-white mx-auto mb-1" />
                              <div className="text-white text-[10px]">Forum</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Decorative elements */}
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#00B050]/20 rounded-full blur-2xl"></div>
                <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
              Les membres du Club témoignent
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Retours d'expérience de notre communauté d'investisseurs
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl border border-slate-100 p-6 hover:shadow-lg transition-shadow duration-300" data-testid={`testimonial-${index}`}>
                <svg className="w-8 h-8 text-[#00B050] mb-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311C9.591 11.69 11.052 13.194 11.052 15c0 1.657-1.343 3-3 3-.834 0-1.632-.372-2.192-.987a2.99 2.99 0 01-1.277.308zM14.583 17.321C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311C19.591 11.69 21.052 13.194 21.052 15c0 1.657-1.343 3-3 3-.834 0-1.632-.372-2.192-.987a2.99 2.99 0 01-1.277.308z"/>
                </svg>
                <p className="text-slate-600 leading-relaxed mb-5">"{testimonial.content}"</p>
                <div className="w-10 h-0.5 bg-[#00B050] mb-3"></div>
                <p className="font-bold text-slate-900">{testimonial.name}</p>
                <p className="text-sm text-slate-500">{testimonial.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0D47A1]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Prêt à rejoindre le Club ?
          </h2>
          <p className="text-xl text-blue-100 mb-4">
            Un cercle fermé d'investisseurs et de professionnels qui font avancer leurs projets ensemble.
          </p>
          <p className="text-base text-blue-200 mb-8">
            Testez gratuitement les outils de base, puis passez au Club pour accéder à tout l'écosystème.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button data-testid="cta-register-btn" size="lg" className="bg-white text-[#0D47A1] hover:bg-slate-100 text-lg px-8 h-12">
                Demander l'accès
              </Button>
            </Link>
            {!isNative && (
            <Link to="/pricing">
              <Button data-testid="cta-pricing-btn" variant="outline" size="lg" className="border-white text-white hover:bg-white/10 text-lg px-8 h-12">
                Voir les tarifs
              </Button>
            </Link>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 items-start">
            <div className="md:col-span-1">
              <div className="mb-6">
                <img src="/logo-footer.png" alt="10 Millions Investor" className="h-24 w-auto" />
              </div>
              <p className="text-sm">
                Le club privé des investisseurs et professionnels de l'immobilier.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Outils</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/calculator" className="hover:text-white transition-colors">Calculateur</Link></li>
                <li><Link to="/mdb" className="hover:text-white transition-colors">Outil MDB</Link></li>
                <li><Link to="/community" className="hover:text-white transition-colors">Communauté</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Ressources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/cgv" className="hover:text-white transition-colors">CGV</Link></li>
                <li><Link to="/faq" className="hover:text-white transition-colors">FAQ</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Légal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/charte" className="hover:text-white transition-colors">Charte de Qualité</Link></li>
                <li><Link to="/confidentialite" className="hover:text-white transition-colors">Confidentialité</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-sm flex flex-col sm:flex-row justify-between items-center">
            <span>© 2026 10 Millions Investor. Tous droits réservés.</span>
            <span className="mt-2 sm:mt-0 text-slate-500">
              Création Plateforme <a href="https://webisula.com" target="_blank" rel="noopener noreferrer" className="text-[#00B050] hover:text-white transition-colors font-semibold">Webisula</a>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
